import type RTKClient from "@cloudflare/realtimekit";
import type { RTKParticipant } from "@cloudflare/realtimekit";
import { RTK_HOST_PRESET } from "./realtimeKitPresets";

export function isHostParticipant({
  participant,
}: {
  participant: Pick<RTKParticipant, "presetName">;
}): boolean {
  return participant.presetName === RTK_HOST_PRESET;
}

export function hasHostLivestream({ participant }: { participant: RTKParticipant }): boolean {
  const hasScreenShare =
    participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video);
  const hasCamera = participant.videoEnabled && Boolean(participant.videoTrack);
  const hasAudio = participant.audioEnabled && Boolean(participant.audioTrack);
  return hasScreenShare || hasCamera || hasAudio;
}

export function meetingHasHostLivestream({ meeting }: { meeting: RTKClient }): boolean {
  return meeting.participants.joined
    .toArray()
    .some(
      (participant) =>
        isHostParticipant({ participant }) &&
        hasHostLivestream({ participant }),
    );
}

export function otherHostHasLivestream({
  meeting,
  selfId,
}: {
  meeting: RTKClient;
  selfId: string;
}): boolean {
  return meeting.participants.joined
    .toArray()
    .some(
      (participant) =>
        participant.id !== selfId &&
        isHostParticipant({ participant }) &&
        hasHostLivestream({ participant }),
    );
}

export function getStageFeedParticipants({
  meeting,
  includeSelf,
}: {
  meeting: RTKClient;
  includeSelf: boolean;
}): RTKParticipant[] {
  const selfId = meeting.self.id;

  const selfOnStage = meeting.stage.status === "ON_STAGE";

  const onStage = meeting.participants.joined.toArray().filter((participant) => {
    if (participant.id === selfId) {
      return includeSelf || participant.stageStatus === "ON_STAGE" || selfOnStage;
    }
    if (participant.stageStatus === "ON_STAGE") return true;
    return false;
  });

  if (
    (includeSelf || selfOnStage) &&
    !onStage.some((participant) => participant.id === selfId)
  ) {
    onStage.unshift(meeting.self as unknown as RTKParticipant);
  }

  return onStage.sort((a, b) => {
    if (a.id === selfId) return -1;
    if (b.id === selfId) return 1;
    if (a.presetName === RTK_HOST_PRESET && b.presetName !== RTK_HOST_PRESET) return -1;
    if (b.presetName === RTK_HOST_PRESET && a.presetName !== RTK_HOST_PRESET) return 1;
    return a.name.localeCompare(b.name);
  });
}

export function getVideoGridLayout({ count }: { count: number }): {
  cols: number;
  rows: number;
} {
  if (count <= 1) return { cols: 1, rows: 1 };
  return { cols: 1, rows: count };
}
